import clsx from 'clsx';
import type { ButtonHTMLAttributes } from 'react';

import type {
  CreateOrderRes,
  ProductParamType,
  ProductShortKeyType,
} from '@/api/payments';
import { useCreateOrder, usePostPaymentDetails } from '@/api/payments';
import type { CreateSubscriptionRes } from '@/api/payments/createSubscription';
import { useCreateSubscription } from '@/api/payments/createSubscription';
import { useVerifySubscription } from '@/api/payments/verifySubscription';
import type {
  PaymentMethodConfig,
  RazorPaySuccessResponse,
} from '@/providers/payments';
import PaymentsProvider from '@/providers/payments';
import type { BaseResponse } from '@/types';

import { useNotificationStore } from '../store';
import ButtonLoader from './UiLoader/ButtonLoader';

export type PaymentButtonProps = {
  text: string;
  className: string;
  customerId: string;
  amount: number;
  productShortKey: ProductShortKeyType;
  onClickHandler?: () => void;
  successHandler: (result: RazorPaySuccessResponse) => void;
  failureHandler: (result: any) => void;
  paymentDescription?: string;
  productParams: ProductParamType;
  paymentMethods: PaymentMethodConfig;
  onOrderCreation?: (
    response:
      | (CreateOrderRes & { orderType: 'ORDER' })
      | (CreateSubscriptionRes & { orderType: 'SUBSCRIPTION' })
  ) => void;
  preOrderCreationHook?: () => void;
  isRepaymentDisabled?: boolean;
};

/**
 * Payment button component
 *
 * @param {PaymentButtonProps} props
 * @returns {JSX.Element}
 */
export const PayButton = ({
  text,
  className,
  customerId,
  amount,
  productShortKey,
  onClickHandler = () => {},
  successHandler,
  failureHandler,
  paymentDescription = '',
  productParams,
  type = 'button',
  paymentMethods,
  preOrderCreationHook = () => {},
  isRepaymentDisabled,
  ...props
}: PaymentButtonProps &
  ButtonHTMLAttributes<HTMLButtonElement>): JSX.Element => {
  const { mutateAsync: createOrderMutate, isLoading: isCreatingOrder } =
    useCreateOrder();

  const { mutateAsync: createSubscription, isLoading: isCreatingSubscription } =
    useCreateSubscription();

  const {
    mutateAsync: postPaymentDetailsMutate,
    isLoading: isPostingPaymentDetails,
  } = usePostPaymentDetails();

  const {
    mutateAsync: verifySubscription,
    isLoading: isVerifyingSubscription,
  } = useVerifySubscription();

  const { addNotification } = useNotificationStore();

  const payload = {
    customerId,
    payload: {
      amount,
      productShortKey,
      productParams,
    },
  };

  /**
   * Click handler for payment button
   */
  const handleClick = async () => {
    onClickHandler();
    preOrderCreationHook();

    /**
     * Create a subscription if the product params contain both 'dg' and 'sip'
     * else create a One time Order
     */
    if (productParams.dg && productParams.sip) {
      const response: BaseResponse<CreateSubscriptionRes> =
        await createSubscription(payload);

      if (response.status == 201) {
        if (typeof props.onOrderCreation === 'function') {
          props.onOrderCreation({
            ...response.data.data,
            orderType: 'SUBSCRIPTION',
          });
        }

        // Proceed for payment after the subscription is created
        PaymentsProvider(
          amount,
          response.data.data.pgKey,
          response.data.data.orderId,
          paymentDescription,
          successHandler,
          failureHandler,
          addNotification,
          postPaymentDetailsMutate,
          verifySubscription,
          response.data.data.orderId,
          customerId,
          productShortKey,
          paymentMethods,
          response.data.data.subscriptionId,
          response.data.data.pgCustomerId // Passing pgCustomerId here to send in options when the flow is SIP
        );
      }
    } else {
      // Creates an order for payment
      const response: BaseResponse<CreateOrderRes> = await createOrderMutate(
        payload
      );

      if (response.status == 201) {
        if (typeof props.onOrderCreation === 'function') {
          props.onOrderCreation({
            ...response.data.data,
            orderType: 'ORDER',
          });
        }
        // Proceed for payment after the order is created
        PaymentsProvider(
          amount,
          response.data.data.gatewayData.key,
          response.data.data.paymentGatewayOrderId,
          paymentDescription,
          successHandler,
          failureHandler,
          addNotification,
          postPaymentDetailsMutate,
          verifySubscription,
          response.data.data.orderId,
          customerId,
          productShortKey,
          paymentMethods
        );
      }
    }
  };

  return (
    <button
      className={clsx(
        className,
        (isCreatingOrder ||
          isCreatingSubscription ||
          isPostingPaymentDetails ||
          isVerifyingSubscription) &&
          'disabled',
        'position-relative'
      )}
      type={type}
      onClick={handleClick}
      {...props}
      disabled={
        isCreatingOrder ||
        isCreatingSubscription ||
        isPostingPaymentDetails ||
        isVerifyingSubscription ||
        isRepaymentDisabled
      }
    >
      {text}
      {(isCreatingOrder ||
        isCreatingSubscription ||
        isPostingPaymentDetails ||
        isVerifyingSubscription) && <ButtonLoader />}
    </button>
  );
};
